/**
 * Shared helpers for the Agreements modals (Org / User / Footer single-doc).
 * Kept framework-agnostic so they can be unit-tested without React.
 */

/**
 * @param {Array} catalogueItems
 * @param {string} documentKind
 */
const getCatalogueItem = (catalogueItems, documentKind) =>
  (catalogueItems || []).find((i) => i.documentKind === documentKind);

/**
 * Cache key used by the legal markdown cache and loading map.
 */
const cacheKey = (documentKind, versionLabel) =>
  `${documentKind}@${versionLabel || 'latest'}`;

/**
 * Return the cached markdown for a document kind, or an empty string.
 */
const getMarkdown = (markdownCache, catalogueItems, documentKind) => {
  const item = getCatalogueItem(catalogueItems, documentKind);
  return (markdownCache || {})[cacheKey(documentKind, item?.versionLabel)] || '';
};

/**
 * Is the markdown for a specific document currently loading?
 */
const isDocLoading = (loading, catalogueItems, documentKind) => {
  const item = getCatalogueItem(catalogueItems, documentKind);
  return Boolean((loading || {})[cacheKey(documentKind, item?.versionLabel)]);
};

/**
 * Is any markdown in the given list currently loading?
 */
const isAnyLoading = (loading, catalogueItems, documentKinds) =>
  (documentKinds || []).some((kind) => isDocLoading(loading, catalogueItems, kind));

/**
 * "terms_of_service" -> "Terms Of Service"
 */
const formatDocumentKind = (kind) =>
  (kind || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Customer-facing titles for legal `documentKind` ids (when catalogue `title` is absent). */
const LEGAL_DOCUMENT_KIND_LABELS = {
  terms_of_service: 'Terms of Service',
  jurisdiction_schedule: 'Jurisdiction Schedule',
  dpa: 'Data Processing Agreement',
  privacy_policy: 'Privacy Policy',
  eula: 'End User Licence Agreement',
  consultant_addendum: 'Consultant Licence Addendum',
  auditor_terms: 'Auditor Terms of Access',
  wl_partner_agreement: 'White Label Partner Agreement',
};

const formatLegalDocumentKind = (documentKind) => {
  const key = String(documentKind || '').toLowerCase();
  return LEGAL_DOCUMENT_KIND_LABELS[key] || formatDocumentKind(documentKind);
};

const CPI_SOURCE_LABELS = {
  UK_CPIH: 'UK CPIH',
  UK_CPI: 'UK CPI',
  UK_RPI: 'UK RPI',
};

/**
 * Human-readable indexation policy for customer-facing pages.
 */
const formatIndexationPolicy = (policy) => {
  if (!policy || policy.kind === 'none') return null;
  const { kind, floorPercent, cpiSource, noticeDays } = policy;
  if (kind === 'fixed_percent') {
    const parts = ['Fixed annual increase'];
    if (floorPercent != null) parts.push(`of ${floorPercent}%`);
    if (noticeDays != null) parts.push(`with ${noticeDays} days' notice`);
    return parts.join(' ');
  }
  if (kind === 'cpi_with_floor') {
    const parts = ['CPI-linked increase'];
    if (floorPercent != null) parts.push(`(minimum ${floorPercent}%)`);
    if (cpiSource) {
      parts.push(`based on ${CPI_SOURCE_LABELS[cpiSource] || formatDocumentKind(cpiSource)}`);
    }
    if (noticeDays != null) parts.push(`with ${noticeDays} days' notice`);
    return parts.join(' ');
  }
  return formatDocumentKind(kind);
};

/** Consultant-only legal document; hide on non-consultant commercial agreements. */
const isConsultantOnlyDocument = (documentKind) => documentKind === 'consultant_addendum';

const isConsultantAgreementType = (agreementType) =>
  String(agreementType || '').toLowerCase() === 'consultant';

/**
 * Legal documents shown on the public agreement page (filters consultant addendum).
 */
const filterPublicLegalDocuments = (documents, agreementType) =>
  (documents || []).filter(
    (doc) => !isConsultantOnlyDocument(doc.documentKind) || isConsultantAgreementType(agreementType),
  );

const formatLegalDocumentTitle = (doc) =>
  doc?.title || formatLegalDocumentKind(doc?.documentKind);

/**
 * Build the payload sent to POST /my/legal/accept.
 */
const buildAcceptPayload = (outstandingDocs, catalogueItems) =>
  (outstandingDocs || []).map((doc) => {
    const item = getCatalogueItem(catalogueItems, doc.documentKind);
    return {
      documentKind: doc.documentKind,
      versionLabel: doc.requiredVersion || item?.versionLabel,
      artifactSha256: item?.sha256 || undefined,
    };
  });

/**
 * Rough reading time estimate. Returns minutes (min 1).
 */
const estimateReadMinutes = (markdown) => {
  if (!markdown) return null;
  const text = String(markdown)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#>*_`~\-|]/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (!words) return null;
  return Math.max(1, Math.round(words / 225));
};

/**
 * Join an array of titles into a natural-language list:
 *   ["A"]           -> "A"
 *   ["A", "B"]      -> "A and B"
 *   ["A", "B", "C"] -> "A, B and C"
 */
const joinTitles = (titles) => {
  const list = (titles || []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

/**
 * Find a country row by internal id (from state.organisation.countries).
 * @param {Array<{ id: string, name?: string, countryAlpha2Code?: string }>} countries
 * @param {string} id
 */
const findCountryById = (countries, id) => {
  if (!id) return null;
  return (countries || []).find((c) => String(c.id) === String(id)) || null;
};

/**
 * ISO alpha-2 for a country id, when known.
 */
const countryIdToAlpha2 = (countries, id) => {
  const row = findCountryById(countries, id);
  const code = row?.countryAlpha2Code;
  return code ? String(code).toUpperCase() : null;
};

/**
 * Query params for GET /my/legal/contracting-entity from country ids (authenticated).
 */
const buildContractingEntityQueryParams = ({
  incorporationCountryId,
  billingCountryId,
  countries,
}) => {
  const params = {};
  const incId = incorporationCountryId || null;
  const billId = billingCountryId || incorporationCountryId || null;
  if (incId) {
    params.incorporationCountryId = String(incId);
    const incCode = countryIdToAlpha2(countries, incId);
    if (incCode) params.incorporationCountryCode = incCode;
  }
  if (billId) {
    params.billingCountryId = String(billId);
    const billCode = countryIdToAlpha2(countries, billId);
    if (billCode) params.billingCountryCode = billCode;
  }
  return params;
};

/**
 * Query params for GET /public/legal/contracting-entity (no org / login).
 * Public route accepts ISO alpha-2 codes and optional whiteLabelId only.
 */
const buildPublicContractingEntityQueryParams = ({
  incorporationCountryId,
  billingCountryId,
  countries,
  whiteLabelId,
}) => {
  const params = {};
  if (whiteLabelId != null && whiteLabelId !== '') {
    params.whiteLabelId = String(whiteLabelId);
  }
  const incId = incorporationCountryId || null;
  const billId = billingCountryId || incorporationCountryId || null;
  const incCode = incId ? countryIdToAlpha2(countries, incId) : null;
  const billCode = billId ? countryIdToAlpha2(countries, billId) : null;
  if (incCode) params.incorporationCountryCode = incCode;
  if (billCode) params.billingCountryCode = billCode;
  return params;
};

/**
 * Country fields for POST /my/agreements (jurisdiction schedule resolution).
 */
const buildAgreementCountryPayload = ({
  incorporationCountryId,
  billingCountryId,
  countries,
}) => {
  const payload = {};
  if (incorporationCountryId) {
    payload.customerRegisteredCountry = String(incorporationCountryId);
  }
  const effectiveBillingId = billingCountryId || incorporationCountryId;
  if (effectiveBillingId) {
    payload.customerBillingCountry = String(effectiveBillingId);
    const alpha2 = countryIdToAlpha2(countries, effectiveBillingId);
    if (alpha2) payload.customerBillingCountryCode = alpha2;
  }
  return payload;
};

/**
 * Display lines for a CYF contracting entity snapshot or preview entity.
 * @returns {{ title: string, lines: string[], isDefault?: boolean } | null}
 */
const formatCyfContractingEntity = (entity) => {
  if (!entity?.entityName) return null;
  const lines = [];
  if (entity.regNo) lines.push(entity.regNo);
  if (entity.registeredAddress) lines.push(entity.registeredAddress);
  if (entity.governingLaw) lines.push(`Governing law: ${entity.governingLaw}`);
  if (entity.courts) lines.push(`Courts: ${entity.courts}`);
  return {
    title: entity.entityName,
    lines,
    isDefault: Boolean(entity.isDefault),
  };
};

/**
 * Split public agreement end-client billing headline into primary fee + subtitle.
 * @param {{ headline?: string } | null | undefined} endClientBilling
 * @returns {{ main: string | null, sub: string | null }}
 */
const formatEndClientBillingHeadline = (endClientBilling) => {
  const headline = endClientBilling?.headline;
  if (!headline) return { main: null, sub: null };
  const splitAt = headline.indexOf(' — ');
  if (splitAt === -1) return { main: headline, sub: null };
  return {
    main: headline.slice(0, splitAt),
    sub: headline.slice(splitAt + 3),
  };
};

/**
 * Normalise public agreement end-client billing for display (handles partial API payloads).
 * @param {{ type?: string, endClientBilling?: object } | null | undefined} payload
 * @returns {{ paragraphs: string[], headline: { main: string | null, sub: string | null } } | null}
 */
const resolvePublicEndClientBillingDisplay = (payload) => {
  if (!payload || payload.type !== 'Consultant') return null;

  const ecb = payload.endClientBilling || {};
  const planType = ecb.defaultPlanType === 'client_subscription' ? 'client_subscription' : 'per_report';

  let paragraphs = Array.isArray(ecb.paragraphs) ? ecb.paragraphs.filter(Boolean) : [];
  if (!paragraphs.length && ecb.summary) {
    paragraphs = [ecb.summary];
  }
  if (!paragraphs.length) {
    if (planType === 'client_subscription') {
      paragraphs = [
        'Each client you add to the platform is set up on a subscription with a minimum 12-month term. The fee for each client is confirmed when they are added and is invoiced to you monthly.',
        'These client fees are separate from, and additional to, the platform licence fee shown above.',
      ];
    } else if (ecb.defaultPerReportFeeLabel) {
      const fee = ecb.defaultPerReportFeeLabel.replace(/ per report$/i, '');
      paragraphs = [
        `You are charged ${fee} for each report generated in the platform for one of your clients. These charges are added up and invoiced to you monthly.`,
        'These client fees are separate from, and additional to, the platform licence fee shown above.',
      ];
    } else {
      paragraphs = [
        'You are charged a per-report fee for each report generated in the platform for your clients. The applicable rate is confirmed with CYF and invoiced to you monthly.',
        'These client fees are separate from, and additional to, the platform licence fee shown above.',
      ];
    }
  }

  let headline = formatEndClientBillingHeadline(ecb);
  if (!headline.main) {
    if (planType === 'client_subscription') {
      headline = { main: 'Per client subscription', sub: 'Billed to you monthly' };
    } else if (ecb.defaultPerReportFeeLabel) {
      headline = {
        main: ecb.defaultPerReportFeeLabel.replace(/ per report$/i, ''),
        sub: 'per report, for each of your clients',
      };
    } else {
      headline = { main: 'Per report', sub: 'Billed to you monthly' };
    }
  }

  return { paragraphs, headline };
};

export {
  cacheKey,
  getCatalogueItem,
  getMarkdown,
  isDocLoading,
  isAnyLoading,
  formatDocumentKind,
  formatLegalDocumentKind,
  LEGAL_DOCUMENT_KIND_LABELS,
  formatIndexationPolicy,
  filterPublicLegalDocuments,
  formatLegalDocumentTitle,
  buildAcceptPayload,
  estimateReadMinutes,
  joinTitles,
  findCountryById,
  countryIdToAlpha2,
  buildContractingEntityQueryParams,
  buildPublicContractingEntityQueryParams,
  buildAgreementCountryPayload,
  formatCyfContractingEntity,
  formatEndClientBillingHeadline,
  resolvePublicEndClientBillingDisplay,
};
